import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import {
    ConfigurationService,
    CoursesService,
    PathsService,
    PortalConfigurationService,
    UserManagementService
} from '@draeger-academy/common';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { BookingDetails } from '../../../../../../models/bookingDetails';
import { Person } from '../../../../../../models/person';

@Component({
    selector: 'da-lib-booking-detail',
    templateUrl: './account-booking-detail.component.html',
    styleUrls: ['./account-booking-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountBookingDetailComponent implements OnInit {
    @Input() courseId: string;
    @Input() hasDates: boolean;
    private _$bookingDetails: BehaviorSubject<BookingDetails> = new BehaviorSubject<BookingDetails>(null);
    private _$bookingPerson: BehaviorSubject<Person> = new BehaviorSubject<Person>(null);
    private _$billingPerson: BehaviorSubject<Person> = new BehaviorSubject<Person>(null);
    private _$showDownloadErrorMessage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _$activeDownloadButton: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public countryName: string;
    public courseTitle: string;
    linkAccountCourseList: string;
    public salutationData: any;
    public countriesData: any;

    // Download selection state
    public selectAllDownloads = false;
    public selectedDownloads: { [id: string]: boolean } = {};

    constructor(
        private _coursesService: CoursesService,
        private _pathsService: PathsService,
        public _translateService: TranslateService,
        private _configurationService: ConfigurationService,
        private userService: UserManagementService,
        private _courseService: CoursesService,
        private _portalConfigurationService: PortalConfigurationService
    ) {}

    public get bookingDetails$(): Observable<BookingDetails> {
        return this._$bookingDetails.asObservable();
    }

    public get bookingPerson$(): Observable<Person> {
        return this._$bookingPerson.asObservable();
    }

    public get billingPerson$(): Observable<Person> {
        return this._$billingPerson.asObservable();
    }
    private _$translatedBookingSalutation: BehaviorSubject<string> = new BehaviorSubject<string>('');

    public get translatedBookingSalutation$(): Observable<string> {
        return this._$translatedBookingSalutation.asObservable();
    }
    private _$translatedBookingCountry: BehaviorSubject<string> = new BehaviorSubject<string>('');

    public get translatedBookingCountry$(): Observable<string> {
        return this._$translatedBookingCountry.asObservable();
    }

    private _$translatedBillingSalutation: BehaviorSubject<string> = new BehaviorSubject<string>('');

    public get translatedBillingSalutation$(): Observable<string> {
        return this._$translatedBillingSalutation.asObservable();
    }
    private _$translatedBillingCountry: BehaviorSubject<string> = new BehaviorSubject<string>('');

    public get translatedBillingCountry$(): Observable<string> {
        return this._$translatedBillingCountry.asObservable();
    }

    public get showDownloadErrorMessage$(): Observable<boolean> {
        return this._$showDownloadErrorMessage.asObservable();
    }

    public get activeDownloadButton$(): Observable<boolean> {
        return this._$activeDownloadButton.asObservable();
    }

    private _$dateFormat: BehaviorSubject<string> = new BehaviorSubject('');
    public get dateFormat$(): Observable<string> {
        return this._$dateFormat.asObservable();
    }

    private _$status: BehaviorSubject<string> = new BehaviorSubject('');
    public get status$(): Observable<string> {
        return this._$status.asObservable();
    }

    ngOnInit() {
        this._courseService.languageSelected.subscribe((language) => {
            this.translateSalutationsAndCountries(this._$bookingPerson.getValue(), this._$billingPerson.getValue());
        });
        this.linkAccountCourseList = this._pathsService.getAccountCourseList();

        if (this.courseId) {
            this._coursesService.getBookingDetails(this.courseId).then(
                (bookingDetails) => {
                    if (bookingDetails) {
                        if (bookingDetails.downloads?.length > 0) {
                            bookingDetails.downloads = bookingDetails.downloads.map((download, index) => ({ ...download, index })) ?? [];
                        }
                        this._$bookingDetails.next(bookingDetails);
                        this._$bookingPerson.next(bookingDetails.bookingPerson);
                        this._$billingPerson.next(bookingDetails.billingPerson);
                        if (bookingDetails.bookingPerson && bookingDetails.billingPerson) {
                            this.translateSalutationsAndCountries(this._$bookingPerson.getValue(), this._$billingPerson.getValue());
                        }
                        this.courseTitle = bookingDetails.title;
                        if (bookingDetails.unicodeTitle && bookingDetails.unicodeTitle !== null && bookingDetails.unicodeTitle !== '') {
                            this.courseTitle = bookingDetails.unicodeTitle;
                        } else {
                            this.courseTitle = bookingDetails.title;
                        }

                        if (this.hasDates) {
                            setTimeout(() => {
                                const tabSecond = <HTMLElement>document.querySelector('.tab-section .tabs-wrapper .tabs li:last-of-type');
                                tabSecond.click();
                            }, 100);
                        }
                        this._$status.next(bookingDetails.status);
                        this._configurationService.$siteSettings.subscribe((data) => {
                            if (data && data.length > 0) {
                                const _countriesData = data.filter((f) => f.key.toLowerCase() === 'countries')[0];
                                _countriesData['value'].filter((obj) => {
                                    if (obj.code === bookingDetails.bookingPerson.country) {
                                        this.countryName = obj.name;
                                    }
                                });
                            }
                        });

                        // Scroll to #downloads anchor if URL fragment is 'downloads'
                        if (window && window.location && window.location.hash === '#downloads') {
                            setTimeout(() => {
                                const el = document.getElementById('downloads');
                                if (el) {
                                    const yOffset = -50;
                                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                    window.scrollTo({ top: y, behavior: 'smooth' });
                                }
                            }, 200);
                        }
                    }
                },
                (error) => {
                    console.log(error);
                }
            );
            if (this._configurationService.isPartnerPage()) {
                this._configurationService.currentPortal$.subscribe((portalId) => {
                    if (portalId) {
                        this._portalConfigurationService.loadPortalPreferences(portalId).then((preferences) => {
                            const _dateFormat = this._portalConfigurationService.getPortalDateFormat(preferences);
                            this._$dateFormat.next(_dateFormat);
                        });
                    }
                });
            } else {
                this._$dateFormat.next('dd.MM.yyyy');
            }
        }
    }

    private translateSalutationsAndCountries(bookingperson, billingperson) {
        this.salutationData = this._configurationService.$allSalutationsByLanguage.getValue();
        this.countriesData = this._configurationService.$allCountriesByLanguage.getValue();
        const salutationBooking = this.salutationData.filter((item) => item.key === bookingperson.salutation);
        const salutationBilling = this.salutationData.filter((item) => item.key === billingperson.salutation);
        const countryBooking = this.countriesData.filter((item) => item.key === bookingperson.country);
        const countryBilling = this.countriesData.filter((item) => item.key === billingperson.country);
        this._$translatedBookingSalutation.next(salutationBooking[0]?.value);
        this._$translatedBookingCountry.next(countryBooking[0].value);
        this._$translatedBillingSalutation.next(salutationBilling[0]?.value);
        this._$translatedBillingCountry.next(countryBilling[0].value);
    }

    // Download selection state
    get downloads() {
        const details = this._$bookingDetails.getValue();
        return details?.downloads?.map((download, index) => ({ ...download, index })) ?? [];
    }

    private isDownloadButtonActive(): boolean {
        return this.downloads.some((d) => d.downloadable && this.selectedDownloads[d.index]);
    }

    onSelectAllDownloadsChange(checked: boolean) {
        this.selectAllDownloads = checked;
        this.downloads.forEach((d) => (this.selectedDownloads[d.index] = d.downloadable ? checked : false));
        this._$activeDownloadButton.next(this.isDownloadButtonActive());
    }

    onDownloadCheckboxChange(index: string, checked: boolean) {
        this.selectedDownloads[index] = checked;
        this.selectAllDownloads = this.downloads.every((d) => this.selectedDownloads[d.index]);
        this._$activeDownloadButton.next(this.isDownloadButtonActive());
    }

    onDownloadSelected(): void {
        this._$showDownloadErrorMessage.next(false);
        const downloads = {
            enrolmentId: this._$bookingDetails.getValue().id,
            downloadFileIds: this.downloads
                .filter((download) => this.selectedDownloads[download.index])
                .map((download) => download.materialId)
        };
        let authorization = '';
        if (JSON.parse(localStorage.getItem('token'))) {
            authorization = 'Bearer ' + JSON.parse(localStorage.getItem('token'));
        } else if (JSON.parse(sessionStorage.getItem('token'))) {
            authorization = 'Bearer ' + JSON.parse(sessionStorage.getItem('token'));
        }

        fetch('/api/v1/Booking/DownloadCourseFiles', {
            method: 'POST',
            body: JSON.stringify(downloads),
            headers: {
                'Content-Type': 'application/json',
                Authorization: authorization
            }
        })
            .then((response) => {
                if (!response.ok) {
                    this._$showDownloadErrorMessage.next(true);
                    return;
                }
                return response.blob();
            })
            .then((blob) => {
                if (blob) {
                    this._$showDownloadErrorMessage.next(false);
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'download.zip';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                }
            })
            .catch((error) => console.error('Download failed:', error));
    }
}
